import React from "react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { cellText } from "../utils/helpers";

const ADMIN_NAV = [
  { section: "Overview" },
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { id: "report", label: "Generate Report", icon: "📈" },
  { id: "analyze", label: "Analyze", icon: "🔍" },
  { section: "Tasks" },
  { id: "all-tasks", label: "All Tasks", icon: "📋" },
  { id: "pending", label: "Pending", icon: "⏳", badge: "pending" },
  { id: "completed", label: "Completed", icon: "✅" },
  { id: "overdue", label: "Overdue", icon: "🔴", badge: "overdue" },
  { id: "recurring", label: "Recurring Tasks", icon: "🔄" },
  { id: "monitor", label: "Monitor", icon: "👁️" },
  { section: "Actions" },
  { id: "create-task", label: "Create Task", icon: "✨" },
  { section: "People" },
  { id: "users", label: "Employees", icon: "👥" },
];

const EMPLOYEE_NAV = [
  { section: "Overview" },
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { section: "My Tasks" },
  { id: "all-tasks", label: "All Tasks", icon: "📋" },
  { id: "pending", label: "Pending", icon: "⏳", badge: "pending" },
  { id: "completed", label: "Completed", icon: "✅" },
  { id: "overdue", label: "Overdue", icon: "🔴", badge: "overdue" },
  { section: "Activity" },
  { id: "created", label: "Created Tasks", icon: "📝" },
  { id: "monitor", label: "Monitor", icon: "👁️" },
  { section: "Actions" },
  { id: "create-task", label: "Create Task", icon: "✨" },
];

export default function Sidebar({ activePage, onNavigate, isOpen, onClose, adminViewMode, onToggleViewMode }) {
  const { user, logout } = useAuth();
  const { adminRecords, userRecords } = useData();

  const [syncing, setSyncing] = React.useState(false);
  const { getToken } = useAuth();
  const { refresh } = useData();

  const handleSyncDb = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await fetch("https://aquint-task-management.aged-wildflower-4467.workers.dev/api/sync", {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert(`Successfully synced Lark to D1!\nAdmin Tasks Synced: ${data.adminSynced}\nUser Tasks Synced: ${data.userSynced}`);
        refresh(); // Refresh state immediately to load new data
      } else {
        alert(`Sync failed: ${data.error || "Unknown error"}`);
      }
    } catch (e) {
      alert(`Sync error: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const counts = React.useMemo(() => {
    if (user?.role === "admin" && adminViewMode === "admin") {
      const pending = adminRecords.filter(r => {
        const s = cellText(r.fields?.["Overall Status"]).toLowerCase();
        const isDone = s.includes("done") || s.includes("✅") || s.includes("complet");
        const isOverdue = s.includes("overdue") || s.includes("🔴");
        const isPending = s.includes("not started") || s.includes("⬜") || s.includes("progress") || s.includes("🟡") || s === "";
        return isPending && !isDone && !isOverdue;
      }).length;
      const overdue = adminRecords.filter(r => {
        const s = cellText(r.fields?.["Overall Status"]).toLowerCase();
        const isOverdue = s.includes("overdue") || s.includes("🔴");
        const isDone = s.includes("done") || s.includes("✅") || s.includes("complet");
        return isOverdue && !isDone;
      }).length;
      return { pending, overdue };
    } else {
      // Build admin-done lookup so counts match the page filters
      const adm = {};
      for (const ar of adminRecords) {
        const tid = cellText(ar.fields?.["Task ID"]);
        if (!tid || tid === "—") continue;
        const st = cellText(ar.fields?.["Overall Status"]).toLowerCase();
        adm[tid] = st.includes("done") || st.includes("✅");
      }
      const mine = userRecords.filter(r => r.user_id === user?.userId);
      const pending = mine.filter(r => !adm[cellText(r.fields?.["Task ID"])]).length;
      const overdue = mine.filter(r => {
        const end = r.fields?.["End Date"];
        const isPast = end && end < Date.now();
        return isPast && !adm[cellText(r.fields?.["Task ID"])];
      }).length;
      return { pending, overdue };
    }
  }, [user, adminViewMode, adminRecords, userRecords]);

  const nav = (user?.role === "admin" && adminViewMode === "admin") ? ADMIN_NAV : EMPLOYEE_NAV;

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? "visible" : ""}`} onClick={onClose} />
      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-logo" style={{ display: "flex", flexDirection: "column", gap: "10px", paddingBottom: "12px" }}>
          <div className="sidebar-logo-inner">
            <div className="logo-icon">🗂️</div>
            <div>
              <h2>Aquint Infratele</h2>
              <p>
                {user?.role === "admin" 
                  ? (adminViewMode === "admin" ? "Admin Panel" : "Employee Panel") 
                  : "Employee Panel"
                }
              </p>
            </div>
          </div>

          {user?.role === "admin" && (
            <button
              onClick={onToggleViewMode}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: "var(--surface-1)",
                border: "1.5px solid var(--border-default)",
                borderRadius: "8px",
                color: "var(--text-secondary)",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginTop: "4px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--surface-1)"}
            >
              <span>🔄</span>
              <span>Switch to {adminViewMode === "admin" ? "Employee View" : "Admin View"}</span>
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {nav.map((item, i) => {
            if (item.section) return <div key={i} className="nav-section-title">{item.section}</div>;
            const isSync = item.id === "sync-db";
            return (
              <div key={item.id}
                className={`nav-item ${activePage === item.id ? "active" : ""} ${isSync && syncing ? "nav-item-syncing" : ""}`}
                onClick={() => {
                  if (isSync) {
                    handleSyncDb();
                  } else {
                    onNavigate(item.id);
                  }
                  onClose?.();
                }}
              >
                <span className="nav-icon">{isSync && syncing ? "🔄" : item.icon}</span>
                <span style={{ flex: 1 }}>{isSync && syncing ? "Syncing..." : item.label}</span>
                {item.badge && counts[item.badge] > 0 && (
                  <span className={`nav-badge nav-badge-${item.badge}`}>
                    {counts[item.badge]}
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip" onClick={logout} title="Click to logout">
            <div className={`avatar avatar-sm avatar-${user?.role}`}>
              {user?.initials || user?.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user?.name}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "capitalize" }}>
                {user?.role} · Tap to logout
              </div>
            </div>
            <span style={{ fontSize: 14, color: "var(--text-tertiary)" }}>🚪</span>
          </div>
        </div>
      </div>
    </>
  );
}
